import type { AxiosRequestConfig } from 'axios';
import uuid from 'react-uuid';

import type { ExportFeedbackParams } from '@/features/Feedback/components/FeedbackListModal/types';
import mockupUtils from '@/utils/mockupUtils';
import { isEmptyString, timeOut } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { defaultAxios } from '..';

const globalSearchService = {
  async submitSearch(params: GlobalSearchPayload): Promise<GlobalSearchResult> {
    enum SearchType {
      LEXICAL = 0,
      SEMANTIC = 1,
    }
    const wa = getCurrWorkspaceAlias();

    const res = await defaultAxios.post(`/workspaces/${wa}/search`, {
      ...params,
      type: isEmptyString(params.query)
        ? SearchType.LEXICAL
        : SearchType.SEMANTIC,
    });
    return res.data;
  },
  async fetchSearchResult(alias: string): Promise<GlobalSearchResult> {
    const res = await defaultAxios.get(`/searches/${alias}`);

    return res.data;
  },
  async updateSearchTitle(
    alias: string,
    title: string,
  ): Promise<GlobalSearchResult> {
    try {
      const res = await defaultAxios.patch(`/searches/${alias}/title`, {
        title,
      });
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async updateSearchOptions(
    alias: string,
    options: GlobalSearchOptions,
  ): Promise<GlobalSearchResult> {
    try {
      const res = await defaultAxios.patch(`/searches/${alias}/options`, {
        options,
      });
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async deleteSearchHistoryItem(alias: string): Promise<void> {
    try {
      await defaultAxios.delete(`/searches/${alias}`);
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchSearchHistoryList(cursor?: string): Promise<GlobalSearchResult[]> {
    const config: AxiosRequestConfig = {};

    if (cursor) {
      config.params = {
        cursor,
      };
    }

    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.get(`/workspaces/${wa}/searches`, config);
    return res.data.searches;
  },

  async fetchSearchFeedbackList(
    alias: string,
    params: {
      query: {
        cursor?: string | null;
        sortby?: string | null;
        order?: OrderStatusT | null;
      };
      payload: StructuredFilter;
    },
  ): Promise<GlobalSearchFeedbackListResponseDTO> {
    const { query, payload } = params;
    const cursor = query.cursor;
    const sortby = query.sortby || 'date';
    const order = query.order || 'desc';

    const res = await defaultAxios.post(
      `/searches/${alias}/feedbacks`,
      payload,
      {
        params: {
          cursor,
          sortby,
          order,
        },
      },
    );
    return res.data;
  },
  async fetchSearchFeedbackTrend(alias: string): Promise<StatisticDataT> {
    const res = await defaultAxios.get(`/searches/${alias}/feedback_trend`);
    return res.data;
  },
  async fetchFeedbackSentimentDist(alias: string): Promise<StatisticDataT> {
    const res = await defaultAxios.get(`/searches/${alias}/sentiment_dist`);
    return res.data;
  },

  async fetchRelatedTagList(alias: string): Promise<StatisticDataT> {
    const res = await defaultAxios.get(`/searches/${alias}/top_tags`);
    return res.data;
  },

  // Account name이 고유성을 보장하지 못하기 때문에, 이름에 대한 aliasList를 받도록 처리함
  async fetchRelatedAccountList(alias: string): Promise<{
    total: number;
    buckets: (DataBucketT & {
      aliasList: string[];
    })[];
  }> {
    const res = await defaultAxios.get(`/searches/${alias}/top_accounts`);
    return res.data;
  },

  async fetchAccountDist(
    alias: string,
    field_name: string,
  ): Promise<StatisticDataT> {
    const res = await defaultAxios.get(`/searches/${alias}/account_dist`, {
      params: {
        field_name,
      },
    });
    return res.data;
  },
  async fetchContactDist(
    alias: string,
    field_name: string,
  ): Promise<StatisticDataT> {
    const res = await defaultAxios.get(`/searches/${alias}/contact_dist`, {
      params: {
        field_name,
      },
    });
    return res.data;
  },
  async fetchAgentDist(
    alias: string,
  ): Promise<GlobalSearchAgentTrendResponseDTO> {
    const res = await defaultAxios.get(`/searches/${alias}/agent_dist`);
    return res.data;
  },
  async fetchTopicTrend(
    alias: string,
    queryParams: TopicTrendRequestDTO,
  ): Promise<TopicTrendItemDTO[]> {
    const res = await defaultAxios.get(`/searches/${alias}/topic_trend`, {
      params: queryParams,
    });
    return res.data;
  },

  async fetchTextSearchSandbox(params: {
    query: string;
    cursor: string | null;
  }): Promise<TextSearchSandboxRes> {
    const mock = false;
    if (mock) {
      await timeOut(500);
      return mockupUtils.generateContent(30, () => {
        const sentence = mockupUtils.lorem.generateSentences(2);
        const len = sentence.length;
        const pos = mockupUtils.getRandomInteger(0, len - 20);
        const matched = sentence.slice(pos, pos + 5);
        return {
          alias: uuid(),
          tid: `FB-${mockupUtils.getRandomInteger(0, 10000).toString()}`,
          title: sentence,
          highlight: matched,
          paginate_token: uuid(),
        };
      });
    }
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.get(`/workspaces/${wa}/test_search`, {
      params: {
        chunks: params.query,
        cursor: params.cursor,
      },
    });
    return res.data;
  },

  async fetchSummaryIssueFeedbackList(
    alias: string,
    issueIndex: number,
    subIssueIndex?: number,
    sortStatus?: SortStatusT,
  ): Promise<SummaryIssueFeedbackListRes> {
    if (typeof subIssueIndex === 'number') {
      const res = await defaultAxios.get(
        `/searches/${alias}/issues/${issueIndex}/subissues/${subIssueIndex}/feedbacks`,
        {
          params: {
            sortby: sortStatus?.sortBy,
            order: sortStatus?.orderStatus,
          },
        },
      );
      return res.data;
    } else {
      const res = await defaultAxios.get(
        `/searches/${alias}/issues/${issueIndex}/feedbacks`,
        {
          params: {
            sortby: sortStatus?.sortBy,
            order: sortStatus?.orderStatus,
          },
        },
      );
      return res.data;
    }
  },

  async fetchFeedbackFieldDist(
    alias: string,
    field_name: string,
  ): Promise<StatisticDataT> {
    const res = await defaultAxios.get(`/searches/${alias}/feedback_dist`, {
      params: {
        field_name,
      },
    });
    return res.data;
  },

  async exportSearchFeedbackList(params: ExportFeedbackParams) {
    const { alias, filterPayload, password, reason } = params;
    await defaultAxios.post(`/searches/${alias}/feedbacks/export`, {
      password: password,
      filter_set: filterPayload,
      reason: reason,
    });
  },

  async exportSummaryIssueFeedbackList(params: {
    search_alias: string;
    issue_index: number;
    password?: string;
    reason?: string;
  }) {
    const { search_alias, issue_index, password, reason } = params;
    await defaultAxios.post(
      `/searches/${search_alias}/issues/${issue_index}/feedbacks/export`,
      {
        password: password,
        reason: reason,
      },
    );
  },

  async exportSummarySubIssueFeedbackList(params: {
    search_alias: string;
    issue_index: number;
    subissue_index: number;
    password?: string;
    reason?: string;
  }) {
    const { search_alias, issue_index, subissue_index, password, reason } =
      params;
    await defaultAxios.post(
      `/searches/${search_alias}/issues/${issue_index}/subissues/${subissue_index}/feedbacks/export`,
      {
        password: password,
        reason: reason,
      },
    );
  },
};

export default globalSearchService;
